import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Rocket,
  Palette,
  Music,
  Eye,
  Sparkles,
  Building2,
  Star,
  Crown,
  ArrowRight,
  Home,
  MapPin,
  Calendar
} from 'lucide-react';
import SEO from './SEO';
import { EditableText, EditableImage, EditableSection, useAdmin } from './AdminContentEditor';

// HTHF Logo
const HTHF_LOGO = 'https://customer-assets.emergentagent.com/job_74632013-6c46-47ca-b50d-529d9afc8cf5/artifacts/j401uj5d_2.png';

// OG Image - white logo on black for better visibility in previews
const OG_IMAGE = 'https://customer-assets.emergentagent.com/job_ai-workshop-live/artifacts/oj9sirfv_2.png';

// Section wrapper with fade-in animation
const Section = ({ children, className = "", id }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      id={id}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      {children}
    </motion.section>
  );
};

// Chrome CTA Button
const ChromeCTAButton = ({ href, children, className = "" }) => {
  const content = (
    <motion.div
      className={`relative inline-flex items-center gap-3 px-8 py-4 rounded-full font-medium text-black overflow-hidden cursor-pointer ${className}`}
      style={{
        background: 'linear-gradient(90deg, #E8E8E8 0%, #C0C0C0 25%, #E8E8E8 50%, #D4D4D4 75%, #E8E8E8 100%)',
        backgroundSize: '200% 100%',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.5)',
      }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      animate={{ backgroundPosition: ['0% 0%', '100% 0%', '0% 0%'] }}
      transition={{ 
        backgroundPosition: { duration: 3, repeat: Infinity, ease: 'linear' },
        scale: { duration: 0.2 }
      }}
    >
      {children}
      <ArrowRight className="w-5 h-5" />
    </motion.div>
  );

  if (href) {
    return <Link to={href}>{content}</Link>;
  }
  return content;
};

// Thin line divider
const Divider = () => (
  <div className="w-full h-px bg-black/10 my-16 md:my-24" />
);

// Section header
const SectionHeader = ({ children, className = "" }) => (
  <h2 className={`text-2xl md:text-3xl font-bold tracking-tight text-black ${className}`}>
    {children}
  </h2>
);

// Sponsor tier card
const SponsorCard = ({ tier, price, tagline, summary, benefits, icon: Icon, featured = false }) => (
  <motion.div
    className={`px-6 py-8 rounded-2xl border ${featured ? 'border-black bg-black text-white' : 'border-black/10 bg-white'}`}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    whileHover={{ y: -4 }}
    transition={{ duration: 0.3 }}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${featured ? 'bg-white/10' : 'bg-black/5'}`}>
      <Icon className={`w-6 h-6 ${featured ? 'text-white' : 'text-black'}`} strokeWidth={1.5} />
    </div>
    <h3 className={`text-xl font-bold mb-2 whitespace-nowrap ${featured ? 'text-white' : 'text-black'}`}>{tier}</h3>
    <p className={`text-3xl font-bold mb-1 ${featured ? 'text-white' : 'text-black'}`}>{price}</p>
    <p className={`text-xs mb-4 ${featured ? 'text-white/50' : 'text-black/40'}`}>USD</p>
    <p className={`text-[11px] font-medium mb-3 whitespace-nowrap ${featured ? 'text-white/90' : 'text-black/80'}`}>{tagline}</p>
    <p className={`text-xs mb-5 ${featured ? 'text-white/60' : 'text-black/50'}`}>{summary}</p>
    <ul className="space-y-2">
      {benefits.map((benefit, i) => (
        <li key={i} className={`flex items-start gap-2 text-xs ${featured ? 'text-white/70' : 'text-black/60'}`}>
          <div className={`w-1 h-1 rounded-full mt-1.5 flex-shrink-0 ${featured ? 'bg-white/50' : 'bg-black/30'}`} />
          {benefit}
        </li>
      ))}
    </ul>
  </motion.div>
);

// Coming Soon Component for unpublished pages
const ComingSoonPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-white text-black px-4">
    <div className="text-center max-w-md">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="w-20 h-20 rounded-2xl bg-black/5 flex items-center justify-center mx-auto mb-6">
          <Users className="w-10 h-10 text-black/40" />
        </div>
        <h1 className="text-4xl font-bold mb-4">Coming Soon</h1>
        <p className="text-black/60 mb-8">
          This page is not yet available. Check back later for updates.
        </p>
        <Link 
          to="/"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-black text-white font-medium hover:bg-gray-800 transition-colors"
        >
          <Home className="w-4 h-4" />
          Back to Home
        </Link>
      </motion.div>
    </div>
  </div>
);

export const HTHFPartnerPage = () => {
  // Check publish status from localStorage
  const [isPublished, setIsPublished] = useState(() => {
    const saved = localStorage.getItem('dd_page_publish_status');
    if (saved) {
      const status = JSON.parse(saved);
      return status['hthf-partner'] === true;
    }
    return false; // Default to unpublished
  });
  
  // Check if user is admin (admins can always see the page)
  const { isAdmin, isEditMode } = useAdmin();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // If not published and not admin, show coming soon
  if (!isPublished && !isAdmin) {
    return <ComingSoonPage />;
  }

  return (
    <div className="min-h-screen bg-white text-black">
      <SEO 
        title="High Tech High Fashion | Partnership"
        description="An invitation-only AI and culture gathering during Spring LA Fashion Week. March 11, 2026 in Los Angeles."
        image={OG_IMAGE}
        url="/hthf-partner"
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm border-b border-black/5">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="p-2 -ml-2 rounded-lg hover:bg-black/5 transition-colors">
            <Home className="w-5 h-5 text-black" strokeWidth={1.5} />
          </Link>
          <span className="text-xs tracking-[0.2em] uppercase text-black/50">Partnership</span>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 pt-28 pb-24">
        
        {/* HERO */}
        <EditableSection sectionId="hthf-partner-hero">
          <Section className="text-center mb-16">
            {/* HTHF Logo */}
            <div className="bg-black p-10 md:p-16 mb-10 mx-auto max-w-xl">
              <EditableImage
                sectionId="hthf-partner-hero"
                field="logo"
                defaultSrc={HTHF_LOGO}
                alt="High Tech High Fashion"
                className="h-28 md:h-36 mx-auto object-contain"
              />
            </div>
            
            <div className="flex items-center justify-center gap-6 text-black/60 mb-6">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" strokeWidth={1.5} />
                <EditableText
                  sectionId="hthf-partner-hero"
                  field="location"
                  defaultValue="Los Angeles"
                  className="text-black/60"
                />
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" strokeWidth={1.5} />
                <EditableText
                  sectionId="hthf-partner-hero"
                  field="date"
                  defaultValue="March 11, 2026"
                  className="text-black/60"
                />
              </div>
            </div>
            
            <div className="text-[15px] md:text-2xl text-black/80 leading-relaxed mx-auto mb-4 px-2 md:px-0 text-center">
              <div>
                <EditableText
                  sectionId="hthf-partner-hero"
                  field="tagline1"
                  defaultValue="An invitation-only AI and culture gathering"
                  className="text-[15px] md:text-2xl text-black/80 leading-relaxed"
                />
              </div>
              <div className="md:pl-20">
                <EditableText
                  sectionId="hthf-partner-hero"
                  field="tagline2"
                  defaultValue="during Spring LA Fashion Week."
                  className="text-[15px] md:text-2xl text-black/80 leading-relaxed"
                />
              </div>
            </div>
          </Section>
        </EditableSection>

        <Divider />

        {/* THE ROOM */}
        <EditableSection sectionId="hthf-partner-room">
          <Section id="the-room" className="mb-16">
            <SectionHeader className="mb-8">
              <EditableText
                sectionId="hthf-partner-room"
                field="title"
                defaultValue="THE ROOM"
                className="text-2xl md:text-3xl font-bold tracking-tight text-black"
              />
            </SectionHeader>
            
            <p className="text-lg text-black/70 leading-relaxed mb-8">
              <EditableText
                sectionId="hthf-partner-room"
                field="intro"
                defaultValue="HTHF brings together:"
                className="text-lg text-black/70 leading-relaxed"
              />
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-10">
              {[
                { icon: Palette, text: 'AI artists', field: 'attendee1' },
                { icon: Rocket, text: 'Startup founders and creative technologists', field: 'attendee2' },
                { icon: Music, text: 'Cultural operators across fashion and music', field: 'attendee3' },
                { icon: Building2, text: 'Builders actively shipping in production environments', field: 'attendee4' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-4 p-4 rounded-xl bg-black/[0.02] border border-black/5"
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <item.icon className="w-5 h-5 text-black/40 flex-shrink-0" strokeWidth={1.5} />
                  <span className="text-black/70">
                    <EditableText
                      sectionId="hthf-partner-room"
                      field={item.field}
                      defaultValue={item.text}
                      className="text-black/70"
                    />
                  </span>
                </motion.div>
              ))}
            </div>
            
            <p className="text-lg text-black/60 leading-relaxed">
              <EditableText
                sectionId="hthf-partner-room"
                field="capacity"
                defaultValue="Attendance capped at 75 curated builders to preserve meaningful dialogue and signal density."
                className="text-lg text-black/60 leading-relaxed"
              />
            </p>
          </Section>
        </EditableSection>

        <Divider />

        {/* WHY ALIGN */}
        <EditableSection sectionId="hthf-partner-why">
          <Section id="why-align" className="mb-16">
            <SectionHeader className="mb-8">
              <EditableText
                sectionId="hthf-partner-why"
                field="title"
                defaultValue="WHY ALIGN"
                className="text-2xl md:text-3xl font-bold tracking-tight text-black"
              />
            </SectionHeader>
            
            <p className="text-lg text-black/70 leading-relaxed mb-8">
              <EditableText
                sectionId="hthf-partner-why"
                field="intro"
                defaultValue="HTHF places AI platforms inside real-world cultural production."
                className="text-lg text-black/70 leading-relaxed"
              />
            </p>
            
            <p className="text-lg text-black/70 leading-relaxed mb-6">
              <EditableText
                sectionId="hthf-partner-why"
                field="benefits_label"
                defaultValue="Sponsors gain:"
                className="text-lg text-black/70 leading-relaxed"
              />
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: Users, text: 'Proximity to high-signal builders', field: 'benefit1' },
                { icon: Eye, text: 'Visibility during LA Fashion Week', field: 'benefit2' },
                { icon: Sparkles, text: 'Alignment with AI-native creative operators', field: 'benefit3' },
                { icon: Building2, text: 'Presence inside a curated activation ecosystem', field: 'benefit4' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-4 p-4 rounded-xl bg-black/[0.02] border border-black/5"
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <item.icon className="w-5 h-5 text-black/40 flex-shrink-0" strokeWidth={1.5} />
                  <span className="text-black/70">
                    <EditableText
                      sectionId="hthf-partner-why"
                      field={item.field}
                      defaultValue={item.text}
                      className="text-black/70"
                    />
                  </span>
                </motion.div>
              ))}
            </div>
          </Section>
        </EditableSection>

        <Divider />

        {/* SPONSORSHIP OPTIONS */}
        <EditableSection sectionId="hthf-partner-tiers">
          <Section id="sponsorship" className="mb-16">
            <SectionHeader className="mb-4 text-center">
              <EditableText
                sectionId="hthf-partner-tiers"
                field="title"
                defaultValue="SPONSORSHIP OPTIONS"
                className="text-2xl md:text-3xl font-bold tracking-tight text-black"
              />
            </SectionHeader>
            
            <p className="text-center text-black/50 mb-12">
              <EditableText
                sectionId="hthf-partner-tiers"
                field="subtitle"
                defaultValue="Limited sponsor alignment available."
                className="text-black/50"
              />
            </p>
          </Section>
        </EditableSection>
        
        {/* Wider grid for sponsor cards */}
        <div className="mb-16 -mx-6 md:-mx-20 lg:-mx-32 px-6 md:px-20 lg:px-32">
            <div className="grid md:grid-cols-3 gap-5 max-w-7xl mx-auto">
              {/* Runway Partner */}
              <SponsorCard
                tier="Runway Partner"
                price="$10,000"
                icon={Users}
                tagline="Visibility Across the Cultural Moment"
                summary="Runway Partners are embedded across the official communications and on-site experience of High Tech High Fashion."
                benefits={[
                  'Logo placement across official event communications (flyer + digital assets)',
                  'Brand inclusion in RSVP confirmation communications',
                  'Brand mention during live event programming',
                  'Logo placement in post-event recap email',
                  'On-site brand acknowledgment during programmed moments'
                ]}
              />
              
              {/* Front Row Partner */}
              <SponsorCard
                tier="Front Row Partner"
                price="$15,000"
                icon={Star}
                tagline="Elevated Visibility + Direct Audience Access"
                summary="Front Row Partners move from presence to proximity. This tier combines visibility with curated access to a high-value audience."
                benefits={[
                  'Everything in Runway Partner, plus:',
                  'Post-event attendee follow-up list (opt-in)',
                  'Logo placement on photo moment backdrop (step-and-repeat)',
                  'Elevated on-site acknowledgment during key programming moments',
                  'Priority brand placement across digital assets'
                ]}
              />
              
              {/* Presenting Partner */}
              <SponsorCard
                tier="Presenting Partner"
                price="$30,000"
                icon={Crown}
                featured={true}
                tagline="Category Leadership + Stage Authority"
                summary="The Presenting Partner anchors the event. A leadership position for a brand ready to claim category authority."
                benefits={[
                  'Everything in Front Row Partner, plus:',
                  'Exclusive category designation (e.g., Exclusive AI Partner)',
                  'Premium logo placement across all event communications',
                  'On-screen brand placement during live programming',
                  'On-mic brand moment (introduction of panelists or programming segment)',
                  'Access to curated VIP reception',
                  'Priority alignment for October AI Fashion Show'
                ]}
              />
            </div>
        </div>

        {/* CTA */}
        <Section className="text-center mb-16">
          <ChromeCTAButton href="/hthf">
            <Sparkles className="w-5 h-5" />
            <span>View HTHF Event</span>
          </ChromeCTAButton>
        </Section>

        <Divider />

        {/* HOSTED BY */}
        <Section className="text-center py-8">
          <p className="text-sm tracking-[0.2em] uppercase text-black/40 mb-8">Hosted By</p>
          <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-8 md:gap-16">
            <div className="text-center">
              <p className="text-lg font-medium text-black mb-2">Data Dreaming</p>
              <p className="text-sm text-black/50 leading-relaxed">A community for creative and analytical thinkers<br />building with emerging technology.</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-medium text-black mb-2">CodeWater Studio</p>
              <p className="text-sm text-black/50 leading-relaxed">A creative technology studio exploring AI, design,<br />and future-facing cultural systems.</p>
            </div>
          </div>
        </Section>

        {/* FOOTER */}
        <footer className="text-center py-8">
          <p className="text-sm text-black/30">© 2026 High Tech High Fashion</p>
        </footer>
      </div>
    </div>
  );
};

export default HTHFPartnerPage;
